import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    useDuplicateCustomFieldTemplateMutation,
    useGetAllDataTypesQuery,
    useGetScopesforFieldsQuery,
    useUpdateCustomFieldTemplateMutation,
} from "../../api/formsApiSlice";
import { ActionType, Column, Scope } from "../../types/types"

import DeletePrompt from "../../components/DeletePrompt";
import { useToast } from "../../components/toast/ToastContext";
import AMSListing from "../../components/listing/AMSListing";
import CreateWidgetModal from "../../components/FormBuilder/CustomForm/CreateWidget";
import { useDeleteWidgetMutation, useDuplicateWidgetMutation, useGetAllWidgetsQuery } from "../../api/widgetApiSlice";

const Listing = () => {
    const { data: scopesData = [], isLoading: scopesLoading } = useGetScopesforFieldsQuery({ isVisible: true });
    const { data: allDataTypes, isLoading: isLoadingDataTypes } = useGetAllDataTypesQuery({});
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [isViewWidget, setIsViewWidget] = useState<boolean>(false);
    const [isSystemWidget, setIsSystemWidget] = useState<boolean>(false);

    const [resultSelectedIds, setResultSelectedIds] = useState<number[]>([]);
    const [duplicateWidget, { isLoading: isDuplicateLoading }] = useDuplicateWidgetMutation();
    const staticFormsColumns: Column[] = [
        // { id: "id", label: "Id", isSortable: false },
        { id: "name", label: "Name", isSortable: true },
        { id: "description", label: "Description", },
        { id: "scope", label: "Scope", isSortable: false },
        { id: "fieldsCount", label: "Field Counts" },
        { id: "statusId", label: "Status", isFlag: true },
        { id: "createdAt", label: "Creation Date", isSortable: true },
        { id: "updatedAt", label: "Last Modified", isSortable: true },
        { id: "isStandard", label: "Widget Type", isCustomStandardTags: true },

    ];

    const filterGroups = [
        {
            key: 'isStandard',
            label: 'Widget Type',
            dataType: 'select',
            options: [
                { label: 'System Widgets', value: true },
                { label: 'Custom Widgets', value: false },
            ],
        },
        {
            key: 'createdAt',
            label: 'Creation Date',
            dataType: 'date',
            options: [],
        },
        {
            key: 'updatedAt',
            label: 'Last Modified',
            dataType: 'date',
            options: [],
        },
        {
            key: 'scopeId',
            label: 'Scope',
            dataType: 'select',
            options: scopesData?.scopes?.map((scope: Scope) => ({
                value: scope.id,
                label: scope.displayName,
            })).filter((scope: any) => scope.label !== 'Event') || [],
        },
    ];

    const showToast = useToast();
    const navigate = useNavigate();
    const [showDeletePrompt, setShowDeletePrompt] = useState(false);
    const [itemId, setItemId] = useState<string | null>(null);
    const [showConditionalFieldsModal, setShowConditionalFieldsModal] = useState(false);
    const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
    const [deleteCustomField] = useDeleteWidgetMutation();
    const [duplicateCustomField] = useDuplicateCustomFieldTemplateMutation();
    const [updateCustomField] = useUpdateCustomFieldTemplateMutation();
    const lastParamsRef = useRef<{
        page: number;
        perPage: number;
        sort?: { field: string; direction: "ASC" | "DESC" };
        query?: string;
        filters: Record<string, any>;
    }>({
        page: 1,
        perPage: 10,
        filters: { isStandard: false },
    });

    const [initialFilters, setInitialFilters] = useState({
        ...lastParamsRef.current,
        query: "",
    });

    const { sort, filters, ...rest } = initialFilters;

    const params = {
        ...rest,
        ...filters,
        sort: sort?.field,
        sortDir: sort?.direction,
    };
    const {
        data: AllFields,
        refetch,
        isLoading,
        isFetching,
    } = useGetAllWidgetsQuery(
        { ...params },
        {
            refetchOnMountOrArgChange: true,
        }
    );

    const handleDuplicate = async (id: number) => {
        try {
            const response = await duplicateWidget(id);
            // if (data) {
            //     showToast("Success", data.message, "success");
            //     refetch();
            // }
        } catch (error) {
            console.error(error);
        }
    };



    return (
        <>
            <AMSListing
                showFilters
                // showDateRangeFilter
                // dateRangePlaceholder="Date"
                // dateRangeClassname="border-0"
                // showDivider
                controlsContainerGap="8px"
                // showSelectionButton
                // showActions
                routeKey="widgets"
                noDataTitle="No Widget Found"
                noDataSubtitle="Your field list is currently empty. Start adding new fields to keep everything organized and easy to find."
                filterGroups={filterGroups}
                title="Widgets"
                subtitle="All custom & system widgets will show here"
                columns={staticFormsColumns}
                setSelectedRecordIds={setResultSelectedIds}
                // data={AllFields?.data || []}
                data={AllFields?.data.map((form: any) => ({
                    ...form,
                    scope: form.scope?.displayName
                })) || []}
                totalItems={AllFields?.pagination?.total}
                loading={isLoading || isFetching}
                skeletonHeight={'42px'}
                onAction={useCallback(
                    async (action: ActionType, id: string, state?: string, isStandard?: boolean) => {
                        const numericId = Number(id);
                        if (action === "delete") {
                            setItemId(id);
                            setShowDeletePrompt(true);
                            return;
                        }
                        if (action === "edit") {
                            setIsViewWidget(false);
                            setIsSystemWidget(!!isStandard);
                            setSelectedFieldId(numericId);
                            setShowCreateModal(true);

                            return;
                        }

                        if (action === "view") {
                            setIsSystemWidget(!!isStandard);
                            setSelectedFieldId(numericId);
                            setShowCreateModal(true);
                            setIsViewWidget(true);

                            return;
                        }

                        if (action === "duplicate") {
                            try {
                                const { data }: any = await duplicateWidget({ id: numericId }).unwrap();
                                refetch();
                                showToast(
                                    "Widget Duplicated",
                                    data.message || "Widget duplicated successfully",
                                    "success"
                                );
                            } catch (e: any) {
                                showToast(
                                    "Duplicate Failed",
                                    e?.data?.message || "Failed to duplicate field. Please try again.",
                                    "error"
                                );
                            }

                            return;
                        }
                    },
                    [refetch, duplicateCustomField, showToast, AllFields?.fields]
                )}
                setInitialFilters={setInitialFilters}
            />

            {showDeletePrompt && (
                <DeletePrompt onConfirm={async () => {
                    if (!itemId) return;
                    const numericId = Number(itemId);
                    setShowDeletePrompt(false);

                    try {
                        const { data }: any = await deleteCustomField(numericId);
                        if (data) {
                            refetch();
                            showToast(
                                "Custom Field Deleted",
                                "The selected field has been deleted.",
                                "success"
                            );
                        }
                    } catch {
                        showToast(
                            "Delete Failed",
                            "Failed to delete field. Please try again.",
                            "error"
                        );
                    }

                    setItemId(null);
                }} onCancel={() => {
                    setShowDeletePrompt(false);
                    setItemId(null);
                }} />
            )}

            {showCreateModal && (
                <CreateWidgetModal
                    onClose={() => { setShowCreateModal(false); setSelectedFieldId(null); setIsViewWidget(false); setIsSystemWidget(false); }}
                    isOpen={showCreateModal}
                    onSuccess={() => refetch()}
                    fieldId={selectedFieldId}
                    isSystemWidget={isSystemWidget}
                    isView={isViewWidget}
                />
            )}


        </>
    );
};

export default Listing;
